import type { IHttpClient, ReportError } from '@wix/yoshi-flow-editor';
import {
  listOperations,
  deleteOperation,
  queryOperation,
} from '@wix/ambassador-restaurants-operations-v1-operation/http';
import type { Operation } from '../types/businessTypes';
import { OperationAdapter } from './utils/OperationAdapter';
import { getMonitoredApiCall } from './utils/getMonitoredApiCall';
import type { FedopsLogger } from 'root/utils/monitoring/FedopsLogger';
import type { ErrorHandlerPublicAPI } from '@wix/fe-essentials-viewer-platform/dist/types/exports/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import type { IBIReporterService } from 'root/services/biReporterService';
import { menusState } from 'root/states/MenusState';
import { itemState } from 'root/states/itemsState';
import { getOperationBIEventSettings } from 'root/utils/BIDurationUtils';

export class OperationsClient {
  constructor(
    private httpClient: IHttpClient,
    private errorHandler: ErrorHandlerPublicAPI,
    private biReporterService?: IBIReporterService,
    private fedopsLogger?: FedopsLogger,
    private errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>,
    private reportError?: ReportError
  ) {}

  getOperation = async (
    operationIdPromise?: Promise<string | undefined>
  ): Promise<Operation | undefined> => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const operationId = await operationIdPromise;

      const { data } = await withErrorHandler(() => this.httpClient.request(listOperations({})), {
        errorCodesMap: {},
      });
      if (operationId) {
        const operation = data?.operations?.find((o) => o?.id === operationId);
        if (operation) {
          return OperationAdapter(operation);
        }
      }
      return undefined;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return undefined;
    }
  };

  getOperations = async (): Promise<Operation[] | undefined> => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, this.biReporterService),
        name: 'listOperations',
        callback: () =>
          withErrorHandler(
            () => this.httpClient.request(listOperations({})).then((response) => response.data),
            {
              errorCodesMap: {},
            }
          ),
        fedops: {
          start: this.fedopsLogger?.listOperationsStarted,
          end: this.fedopsLogger?.listOperationsEnded,
        },
        errorMonitor: this.errorMonitor,
        reportError: this.reportError,
        onError: (e) => {
          const err = getResolvedError(e);
          console.error(err);
        },
      });

      return data?.operations?.length
        ? data?.operations
            ?.filter((operation) => operation.enabled)
            .map((operation) => OperationAdapter(operation ?? {}))
        : undefined;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return undefined;
    }
  };

  getOperationsByGroupId = async (
    operationGroupIdPromise: Promise<string | undefined>
  ): Promise<Operation[] | undefined> => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const operationGroupId = await operationGroupIdPromise;
      const { monitorBiEvent: operationDurationBIEvent, params: operationDurationParams } =
        getOperationBIEventSettings(menusState, itemState, this.biReporterService);
      const { data } = await getMonitoredApiCall({
        operationDurationBIEvent,
        operationDurationParams: {
          ...operationDurationParams,
          operationGroup: operationGroupId,
        },
        name: 'listOperationsByGroupId',
        callback: () =>
          withErrorHandler(
            () =>
              this.httpClient
                .request(
                  queryOperation({
                    query: {
                      filter: {
                        operationGroupId,
                        $not: { 'businessLocationDetails.archived': true },
                        enabled: true,
                      },
                    },
                  })
                )
                .then((response) => response.data),
            {
              errorCodesMap: {},
            }
          ),
        fedops: {
          start: this.fedopsLogger?.listOperationsByGroupIdStarted,
          end: this.fedopsLogger?.listOperationsByGroupIdEnded,
        },
        errorMonitor: this.errorMonitor,
        reportError: this.reportError,
        onError: (e) => {
          const err = getResolvedError(e);
          console.error(err);
        },
      });
      return data?.operations?.length
        ? data?.operations.map((operation) => OperationAdapter(operation ?? {}))
        : undefined;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return undefined;
    }
  };

  deleteOperation = async (operationId: string): Promise<boolean> => {
    const { withErrorHandler, getResolvedError } = this.errorHandler ?? {};
    try {
      const { error } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, this.biReporterService),
        name: 'deleteOperation',
        callback: () =>
          withErrorHandler(() => this.httpClient.request(deleteOperation({ operationId })), {
            errorCodesMap: {},
          }),
        fedops: {
          start: this.fedopsLogger?.deleteOperationStarted,
          end: this.fedopsLogger?.deleteOperationEnded,
        },
        errorMonitor: this.errorMonitor,
        reportError: this.reportError,
        onError: (e) => {
          const err = getResolvedError(e);
          console.error(err);
        },
      });
      return !error;
    } catch (e: unknown) {
      return false;
    }
  };
}
